import { Input } from "@components/ui/input";
import { PlusIcon, SearchIcon } from "lucide-react";
import { getHiringCompanyMembers } from "@endpoints/hiringCompany/hiringCompany";
import React, { useEffect, useState } from "react";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import InviteMemberDialog from "./components/InviteMemberDialog";
import { IHiringStaff } from "@endpoints/types/hiringStaff.type";
import {
  HiringCompanyApprovalStatus,
  HiringCompanyType,
} from "@utils/constants";
import TickThinOutline from "@assets/svg/TickThinOutline";
import CloseIcon from "@assets/svg/CloseIcon";
import { updateStaffRequest } from "@endpoints/hiringCompany/staffMember.service";
import {
  EnumStaffRequestApproval,
  IUpdateStaffRequestQueryParams,
} from "@endpoints/types/hiringCompany/staffMember.type";
import { useSelector } from "react-redux";
import { RootState } from "@store/reducers";

const HiringStaffMembers = () => {
  const [staffMembers, setStaffMembers] = useState<IHiringStaff[]>([]);
  const [isStaffMembersLoading, setIsStaffMembersLoading] = useState(false);
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const { user } = useSelector((state: RootState) => state.auth);
  const currentRole = Number(user?.hiringCompanyType);
  const canInvite =
    currentRole === HiringCompanyType.SUPER_ADMIN ||
    currentRole === HiringCompanyType.ADMIN ||
    currentRole === HiringCompanyType.MANAGER;
  const canApprove =
    currentRole === HiringCompanyType.SUPER_ADMIN ||
    currentRole === HiringCompanyType.ADMIN ||
    currentRole === HiringCompanyType.MANAGER;

  async function fetchHiringCompanyMembers() {
    try {
      setIsStaffMembersLoading(true);
      const { success, data } = await getHiringCompanyMembers();
      if (success) {
        setStaffMembers(data?.members);
        console.log("hiring staff members", data);
      }
    } catch (error) {
      console.error("Error: fetching hiring company members", error);
    } finally {
      setIsStaffMembersLoading(false);
    }
  }

  const handleAcceptOrRejectStaff = async (
    params: IUpdateStaffRequestQueryParams
  ) => {
    try {
      await updateStaffRequest(params);
      fetchHiringCompanyMembers();
    } catch (error) {
      console.error("Error: updating staff request", error);
    }
  };

  useEffect(() => {
    fetchHiringCompanyMembers();
  }, []);

  const formatRoleLabel = (roleValue?: number) => {
    switch (roleValue) {
      case HiringCompanyType.SUPER_ADMIN:
        return "Super Admin";
      case HiringCompanyType.ADMIN:
        return "Company Admin";
      case HiringCompanyType.MANAGER:
        return "Hiring Manager";
      case HiringCompanyType.TEAM_MEMBER:
        return "Hiring Team Member";
      default:
        return "-";
    }
  };

  const getStatusLabel = (staffMember: IHiringStaff) => {
    if (staffMember?.isInvitation) return "Invite sent";
    if (
      staffMember?.hiringCompanyApprovalStatus ===
      HiringCompanyApprovalStatus.REQUESTED
    )
      return "Pending";
    if (
      staffMember?.hiringCompanyApprovalStatus ===
      HiringCompanyApprovalStatus.REJECTED
    )
      return "Rejected";
    return "Active";
  };

  return (
    <React.Fragment>
      {isStaffMembersLoading || !staffMembers?.length ? (
        <div className="flex flex-col flex-1 justify-center items-center">
          {isStaffMembersLoading ? (
            <LoadingAnimation className="h-6 w-6" color="#2e2e2e" />
          ) : (
            <div className="flex flex-col items-center justify-center gap-2">
              <span className="font-medium text-sm text-light-black">
                No staff members available.
              </span>
              {canInvite ? (
                <button
                  onClick={() => setIsInviteDialogOpen(true)}
                  className="flex items-center justify-center rounded-md py-2 px-3 gap-2 bg-secondary hover:bg-orange-400 text-white font-semibold text-sm"
                >
                  <PlusIcon className="w-4 h-4" color="white" />
                  Invite Members
                </button>
              ) : null}
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-col flex-1 gap-5">
          {/* Search container */}
          <div className="flex items-center justify-between bg-gray-100 rounded-md border px-5 py-2.5">
            <div className="flex items-center bg-white pr-3 border rounded-md w-2/3">
              <Input
                className="!focus-visible:ring-[3px] border-none !text-xs font-medium"
                placeholder="Search by agent name,reference..."
                // type="number"
              />
              <SearchIcon className="w-4 h-4" color="#2e2e2e" />
            </div>
            {canInvite ? (
              <button
                onClick={() => setIsInviteDialogOpen(true)}
                className="flex items-center justify-center rounded-md py-2 px-3 gap-2 bg-secondary hover:bg-orange-400 text-white font-semibold text-sm"
              >
                <PlusIcon className="w-4 h-4" color="white" />
                Invite Members
              </button>
            ) : null}
          </div>
          {/* Staff Members list */}
          <div className="flex flex-col">
            {/* Table header */}
            <div className="grid grid-cols-[1fr_1fr_0.7fr_0.5fr] border-b pb-3">
              <span className="font-bold text-xs text-light-black">
                Staff members
              </span>
              <span className="font-bold text-xs text-light-black">
                Role
              </span>
              <span className="font-bold text-xs text-light-black">
                Reference
              </span>
              <span className="font-bold text-xs text-light-black text-center">
                Status
              </span>
            </div>
            {/* Table Body */}
            <div className="flex flex-col">
              {staffMembers?.map((staffMember) => {
                const roleValue = Number(
                  staffMember?.role ?? staffMember?.hiringCompanyType
                );
                const statusLabel = getStatusLabel(staffMember);
                const isRequested =
                  staffMember?.hiringCompanyApprovalStatus ===
                  HiringCompanyApprovalStatus.REQUESTED;
                return (
                  <div
                    key={staffMember?._id}
                    className="grid grid-cols-[1fr_1fr_0.7fr_0.5fr] py-4 border-b"
                  >
                    <div className="flex items-center gap-2">
                      {staffMember?.profilePic ? (
                        <img
                          src={staffMember?.profilePic}
                          className="w-8 h-8"
                        />
                      ) : (
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 font-medium text-gray-500">
                          {staffMember?.firstName.charAt(0)}
                        </div>
                      )}
                      <span className="font-medium text-xs text-light-black">
                        {staffMember?.firstName} {staffMember?.lastName}
                      </span>
                    </div>
                    <span className="font-medium text-xs text-light-black flex items-center capitalize">
                      {formatRoleLabel(roleValue)}
                    </span>
                    <span className="font-medium text-xs text-light-black flex items-center">
                      {staffMember?.refNo || "-"}
                    </span>
                    {isRequested && canApprove && !staffMember?.isInvitation ? (
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() =>
                            handleAcceptOrRejectStaff({
                              userId: staffMember?._id,
                              status: EnumStaffRequestApproval.APPROVE,
                            })
                          }
                          className="flex py-2 px-2 items-center justify-center rounded-md bg-green-600 text-xs text-primary font-semibold hover:bg-green-700"
                        >
                          <TickThinOutline className="w-4 h-4" fill="white" />
                        </button>
                        <button
                          onClick={() =>
                            handleAcceptOrRejectStaff({
                              userId: staffMember?._id,
                              status: EnumStaffRequestApproval.REJECT,
                            })
                          }
                          className="flex py-2 px-2 items-center justify-center rounded-md bg-red-600 text-xs text-primary font-semibold hover:bg-red-700"
                        >
                          <CloseIcon className={"w-4 h-4"} fill="white" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center">
                        <span className="rounded-full bg-gray-100 px-3 py-1 text-[11px] font-semibold text-light-black">
                          {statusLabel}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      {/* InviteMemberDialog */}
      {canInvite ? (
        <InviteMemberDialog
          isInviteDialogOpen={isInviteDialogOpen}
          setIsInviteDialogOpen={setIsInviteDialogOpen}
          onInviteSuccess={fetchHiringCompanyMembers}
        />
      ) : null}
    </React.Fragment>
  );
};

export default HiringStaffMembers;
